import { useLayoutEffect } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import "./socialmediaanalysisview.css";

import { dataPoints, d } from "./social_media_posts_datapoints"; 

export const baseColors = ['#000000', '#0A66C2', '#FF0000'];

function SocialMediaVolumeAnalysisChart() {
  useLayoutEffect(() => {
    const root = am5.Root.new("socialMediaVolumeAnalysisChartId");
    if (root._logo) root._logo.dispose();
    root.setThemes([am5themes_Animated.new(root)]);

    // ✅ Global date formatting used by tooltips/labels
    root.dateFormatter.setAll({
      dateFormat: "MMM yyyy",
      dateFields: ["valueX", "openValueX", "value"]
    });

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        pinchZoomX: true,
        layout: root.verticalLayout
      })
    );

    // (optional) title
    chart.children.unshift(
      am5.Label.new(root, {
        text: "",
        fontSize: 18,
        fontWeight: "600",
        x: am5.p50,
        centerX: am5.p50,
        paddingTop: 8,
        paddingBottom: 8
      })
    );

    const legend = chart.children.push(
      am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 8 })
    );

    // ===== Date axis: clamp to Jan→Dec of the data’s year; label every month as "MMM yyyy" =====
    const yr = dataPoints?.length ? new Date(dataPoints[0].date).getFullYear() : new Date().getFullYear();
    const minTs = new Date(yr, 0, 1, 0, 0, 0, 0).getTime();               // Jan 1
    const maxTs = new Date(yr, 11, 31, 23, 59, 59, 999).getTime();        // Dec 31 end

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 24 // small enough so all months fit
    });
    // grid lines at the START of each month cell, so edges align to Jan/Dec
    xRenderer.grid.template.setAll({ location: 0 });

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: "month", count: 1 },
        gridIntervals: [{ timeUnit: "month", count: 1 }], // ✅ tick every month
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, { labelText: "{value.formatDate('MMM yyyy')}" }),
        startLocation: 0,
        endLocation: 1,
        extraMin: 0,
        extraMax: 0,
        strictMinMax: true,
        min: minTs,
        max: maxTs,
        markUnitChange: false      // don’t collapse month labels on unit change
      })
    );

    // ✅ Ensure labels always show "MMM yyyy"
    xRenderer.labels.template.setAll({
      text: "{value.formatDate('MMM yyyy')}",
      populateText: true
    });

    // Y axis
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0.1 }) })
    );
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: "Number of Posts",
        rotation: -90,
        y: am5.p50,
        centerY: am5.p50,
        x: 0,
        centerX: 0,
        paddingRight: 10,
        fontWeight: "500"
      })
    );
    yAxis.set("numberFormatter", am5.NumberFormatter.new(root, { numberFormat: "#,###" }));

    // Cursor
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none", xAxis }));
    cursor.lineY.set("visible", false);

    // ===== Smoothed line series =====
    function makeSmoothedSeries({ name, field, color }) {
      const series = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: field,
          valueXField: "date",
          stroke: am5.color(color),
          fill: am5.color(color),
          tensionX: 0.8,
          seriesTooltipTarget: "bullet",
          tooltip: am5.Tooltip.new(root, {
            labelText: "{name}\n{valueX.formatDate('MMM yyyy')}: {valueY.formatNumber('#,###')}"
          })
        })
      );

      series.strokes.template.setAll({ strokeWidth: 2 });

      series.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 4,
            fill: am5.color(color),
            strokeWidth: 2,
            stroke: root.interfaceColors.get("background")
          })
        })
      );

      series.data.setAll(dataPoints);
      legend.data.push(series);
      series.appear(800);
      return series;
    }

    makeSmoothedSeries({ name: "X",        field: "X",       color: baseColors[0] });
    makeSmoothedSeries({ name: "LinkedIn", field: "LN",      color: baseColors[1] });
    makeSmoothedSeries({ name: "YouTube",  field: "YouTube", color: baseColors[2] });

    chart.appear(1000, 100);
    return () => { root.dispose(); };
  }, []);

  return (
    <>
      <p className="chart-sub-text">Analysis of social media engagement across pre congress, live congress, and post congress phases.</p>
      <div id="socialMediaVolumeAnalysisChartId" className="socialMediaVolumeAnalysis"></div>
    </>
  );
}

export default SocialMediaVolumeAnalysisChart;